import { computed } from "vue";
import type { ComputedRef } from "vue";

import { USER_ROLES } from "./permissions.constants";
import type { Permission } from "./permissions.constants";
import { hasAnyPermission, hasPermissions, hasRoles } from "./permissions.utils";

import { useUserStore } from "@/shared/stores/useUserStore";

// ─────────────────────────────────────────────────────────────────────────────
// Vue composable — use inside components and feature composables.
//
// ── Template usage (reactive via userStore, no computed needed) ───────────────
//   v-if="can(PERMISSIONS.CATALOG_WRITE)"
//   Vue tracks userStore.user as a reactive dependency through the render function.
//
// ── Script usage (wrap in computed when reactivity is needed in JS) ───────────
//   const canWrite = computed(() => can(PERMISSIONS.CATALOG_WRITE))
//   // Use case: watching the value, passing to a child composable, or
//   // reusing in multiple computed expressions without recalculation.
//
// ── What NOT to do ────────────────────────────────────────────────────────────
//   // Don't create computed for single template v-if — direct call is cleaner:
//   const canWrite = computed(...) // ← unnecessary if only used in one v-if
//
// ── Router guards ─────────────────────────────────────────────────────────────
//   Do NOT use this composable in router guards.
//   Call hasPermissions() / hasRoles() from permissions.utils directly instead.
// ─────────────────────────────────────────────────────────────────────────────

export interface UsePermissionsReturn {
  // Returns true if user holds the given permission. Reactive in templates.
  can: (permission: Permission) => boolean
  // Returns true if user holds at least one of the given permissions (OR logic).
  canAny: (permissions: readonly Permission[]) => boolean
  // Returns true if user's role is in the given list (OR logic).
  canRole: (roles: readonly string[]) => boolean
  // Computed ref — use as a watch source or pass to child components as a prop.
  isAdmin: ComputedRef<boolean>
}

export const usePermissions = (): UsePermissionsReturn => {
  const userStore = useUserStore();

  const can = (permission: Permission): boolean =>
    hasPermissions([permission], userStore);

  const canAny = (permissions: readonly Permission[]): boolean =>
    hasAnyPermission(permissions, userStore);

  const canRole = (roles: readonly string[]): boolean =>
    hasRoles(roles, userStore);

  // Provided as a computed ref since ADMIN checks often need to be reactive refs
  // (e.g., shown in header, drives conditional layout blocks).
  const isAdmin = computed(() => userStore.user?.role === USER_ROLES.ADMIN);

  return { can, canAny, canRole, isAdmin };
};
